import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaDatabase, FaChartPie, FaBrain, FaSearchDollar, FaServer, FaCogs,
    FaChartLine, FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight,
    FaPython, FaFilter, FaLightbulb, FaProjectDiagram, FaTable,
    FaChartBar, FaRobot, FaRegSnowflake
} from 'react-icons/fa';
import {
    SiApachespark, SiTableau, SiPandas, SiMysql, SiPython
} from 'react-icons/si';
import heroImage from '../../assets/AI Images (4k) - Freepik (310724441653).png';

const DataAnalyticsPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaChartBar, title: 'Business Intelligence (BI)', desc: 'Transform raw data into actionable insights with interactive dashboards and reports using PowerBI and Tableau.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600' },
        { icon: FaDatabase, title: 'Big Data Engineering', desc: 'Designing robust data pipelines and warehouses (ETL) to handle massive volumes of structured and unstructured data.', image: 'https://images.unsplash.com/photo-1544383215-e65d34622709?w=600' },
        { icon: FaBrain, title: 'Predictive Analytics & AI', desc: 'Forecasting future trends and behaviors using advanced machine learning models and statistical algorithms.', image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=600' },
        { icon: FaSearchDollar, title: 'Financial Analytics', desc: 'Optimizing financial performance through risk assessment, fraud detection, and investment analysis.', image: 'https://images.unsplash.com/photo-1611974765270-ca1258634369?w=600' },
        { icon: FaServer, title: 'Cloud Data Migration', desc: 'Seamlessly migrating on-premise data to secure cloud environments like AWS Redshift, Google BigQuery, or Azure.', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600' },
        { icon: FaCogs, title: 'Data Governance', desc: 'Ensuring data accuracy, security, and compliance with global standards like GDPR and HIPAA.', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600' }
    ];

    // Using Safest Available Icons
    const technologies = [
        { name: 'Python', icon: SiPython, desc: 'Data Science & Processing' },
        { name: 'Power BI', icon: FaChartBar, desc: 'Interactive Visualizations' },
        { name: 'Tableau', icon: FaChartPie, desc: 'Enterprise Reporting' },
        { name: 'Apache Spark', icon: SiApachespark, desc: 'Big Data Processing' },
        { name: 'BigQuery', icon: FaDatabase, desc: 'Serverless Data Warehouse' },
        { name: 'Snowflake', icon: FaRegSnowflake, desc: 'Cloud Data Platform' },
        { name: 'TensorFlow', icon: FaBrain, desc: 'Machine Learning Models' },
        { name: 'SQL / MySQL', icon: SiMysql, desc: 'Relational Database Mgmt' }
    ];

    const process = [
        { step: '01', title: 'Data Discovery', desc: 'Identifying data sources and defining key business metrics to track.' },
        { step: '02', title: 'Data Ingestion', desc: 'Extracting data from various sources (APIs, DBs, Files) into a central lake.' },
        { step: '03', title: 'Processing & ETL', desc: 'Cleaning, transforming, and structuring data for analysis.' },
        { step: '04', title: 'Analysis & Modeling', desc: 'Applying statistical models and algorithms to uncover hidden patterns.' },
        { step: '05', title: 'Visualization', desc: 'Creating intuitive dashboards to present findings to stakeholders.' },
        { step: '06', title: 'Decision Making', desc: 'Empowering leaders to make data-driven decisions based on live insights.' }
    ];

    const flowSteps = [
        { title: 'Collect', icon: FaDatabase, color: 'from-blue-500 to-indigo-600' },
        { title: 'Clean', icon: FaFilter, color: 'from-cyan-400 to-teal-500' },
        { title: 'Analyze', icon: FaSearchDollar, color: 'from-purple-500 to-violet-600' },
        { title: 'Model', icon: FaBrain, color: 'from-orange-400 to-pink-500' },
        { title: 'Visualize', icon: FaChartPie, color: 'from-green-400 to-emerald-600' },
        { title: 'Action', icon: FaLightbulb, color: 'from-yellow-400 to-amber-500' }
    ];

    const faqs = [
        { q: 'What is the difference between specific Data Analytics and Data Science?', a: 'Data Analytics focuses on processing historical data to explain what happened, while Data Science uses advanced algorithms to predict what will happen in the future.' },
        { q: 'Which tools do you use for visualization?', a: 'We primarily use Power BI, Tableau, and custom D3.js dashboards depending on the client\'s specific needs and existing infrastructure.' },
        { q: 'How do you handle data security?', a: 'We follow strict data governance protocols, including encryption at rest and in transit, role-based access control (RBAC), and compliance with GDPR/HIPAA.' },
        { q: 'Can you work with unstructured data?', a: 'Yes, we specialize in processing unstructured data from social media, emails, and logs using NLP and Big Data technologies.' },
        { q: 'Do you offer ongoing support?', a: 'Absolutely, we provide maintenance packages to ensure your data pipelines remain efficient and dashboards are updated with new metrics.' }
    ];

    const whyChooseUs = [
        { title: 'Actionable Insights', desc: 'We don\'t just provide numbers; we provide strategic recommendations.' },
        { title: 'Advanced Technology', desc: 'Utilizing the latest AI/ML models to give you a competitive edge.' },
        { title: 'Scalable Architecture', desc: 'Solutions designed to handle petabytes of data as your business grows.' },
        { title: 'Industry Expertise', desc: 'Deep domain knowledge in Finance, Healthcare, Retail, and Logistics.' },
        { title: 'Data Privacy', desc: 'Uncompromising commitment to keeping your sensitive data secure.' },
        { title: 'Rapid Deployment', desc: 'Agile methodology to deliver MVPs and insights in weeks, not months.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 via-blue-900/80 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-teal-300 mb-3">What We Do &gt; Intelligence</p>
                        <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">Data Analytics & AI</h1>
                        <p className="text-base font-light text-gray-200 leading-relaxed mb-6">
                            Turning raw data into strategic assets. Unlock the power of predictive insights.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="#inquiry" className="px-5 py-2 bg-white text-teal-700 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                                Get Free Consultation
                            </a>
                            <a href="#services" className="px-5 py-2 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-teal-700 transition-all text-sm whitespace-nowrap">
                                Explore Capabilities
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Content / Intro Stats */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">The Data Advantage</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            In today's digital economy, data is the new oil. We help you refine it.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8 text-center divide-x divide-slate-200">
                        {[
                            { label: 'Faster Decisions', val: '5x' },
                            { label: 'Data Processed', val: '10PB+' },
                            { label: 'Accuracy Improvement', val: '40%' },
                            { label: 'ROI Increase', val: '300%' }
                        ].map((stat, idx) => (
                            <div key={idx} className="p-4">
                                <div className="text-4xl font-bold text-teal-600 mb-2">{stat.val}</div>
                                <div className="text-sm text-slate-500 uppercase tracking-wider">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Our Data Stack</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Best-in-class tools for processing, analyzing, and visualizing data.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {technologies.map((tech, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="p-6 bg-slate-50 rounded-sm border border-slate-200 text-center hover:shadow-lg transition-all group"
                            >
                                <tech.icon className="text-5xl mx-auto mb-4 text-teal-600 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{tech.name}</h3>
                                <p className="text-sm text-slate-600">{tech.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Analytics Solutions</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Comprehensive data services to drive your business forward.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-64 overflow-hidden rounded-sm border border-slate-200 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80"></div>
                                </div>
                                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white z-10">
                                    <div>
                                        <service.icon className="text-4xl mb-4 text-teal-300" />
                                        <h3 className="text-xl font-medium mb-2">{service.title}</h3>
                                    </div>
                                    <motion.div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-teal-600 to-blue-600 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                        <p className="text-white text-sm leading-relaxed">{service.desc}</p>
                                        <button className="mt-4 text-white font-medium hover:underline">Learn More →</button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-20 bg-gradient-to-br from-teal-600 via-blue-600 to-indigo-700 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light mb-6">Why Partner With Us?</h2>
                        <p className="text-lg text-teal-100 max-w-3xl mx-auto">
                            We bridge the gap between complex data and strategic business value.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20 hover:bg-white/20 transition-all"
                            >
                                <FaCheckCircle className="text-3xl text-teal-300 mb-4" />
                                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                                <p className="text-teal-100 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Horizontal Colorful with BIG VISIBLE ARROWS) */}
            <section className="py-24 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Data Journey</h2>
                        <p className="text-slate-600">From raw bits to business brilliance.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-1 bg-slate-200 md:rounded-full -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-0 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">

                                    {/* ARROW: Only show BETWEEN steps */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="hidden md:flex absolute top-[30px] -right-[50%] w-full justify-center items-center z-10 pointer-events-none">
                                            <motion.div
                                                animate={{ x: [-3, 3, -3] }}
                                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                            >
                                                <FaChevronRight className="text-2xl text-slate-400 font-bold" />
                                            </motion.div>
                                        </div>
                                    )}

                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5, y: 20 }}
                                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-3xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300 border-4 border-white ring-4 ring-slate-50`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Number Badge */}
                                    <div className="absolute top-0 right-1/2 translate-x-10 -translate-y-0 w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-md z-30">
                                        {idx + 1}
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 group-hover:text-teal-600 transition-colors mt-2">{step.title}</h3>

                                    {/* Mobile Vertical Line */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute bottom-[-40px] left-1/2 w-0.5 h-[40px] bg-slate-300"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-12">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Frequently Asked Questions</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="border border-slate-200 rounded-sm overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                        >
                                            <span className="font-medium text-slate-900">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-teal-600" /> : <FaChevronDown className="text-slate-400" />}
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 py-4 bg-white">
                                                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Get Data Consultation</h2>
                            <p className="text-slate-600 mb-6">Fill out the form below to start your data journey.</p>

                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                                    <input type="text" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-teal-600" placeholder="John Doe" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                        <input type="email" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-teal-600" placeholder="john@example.com" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                                        <input type="tel" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-teal-600" placeholder="+91 98765 43210" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Topic</label>
                                    <select className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-teal-600">
                                        <option>Business Intelligence</option>
                                        <option>Data Engineering</option>
                                        <option>Machine Learning</option>
                                        <option>Data Migration</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                                    <textarea rows="4" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-teal-600" placeholder="Tell us about your data challenges..."></textarea>
                                </div>
                                <button type="submit" className="w-full bg-teal-600 text-white font-medium py-3 rounded-sm hover:bg-teal-700 transition-colors shadow-lg">
                                    Send Inquiry
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 bg-gradient-to-r from-teal-700 to-blue-800 text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6">Ready to Make Data-Driven Decisions?</h2>
                    <p className="text-xl font-light mb-8 text-teal-100">
                        Transform your data into your most valuable asset.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="#inquiry" className="inline-block px-6 py-2.5 bg-white text-teal-800 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                            Start Now
                        </a>
                        <a href="tel:+919876543210" className="inline-block px-6 py-2.5 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-teal-800 transition-all text-sm whitespace-nowrap">
                            Call Expert
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default DataAnalyticsPage;
